import React, { useEffect, useRef, useState } from "react"
import FilterTabs from "../partials/FilterTabs"
import Stats from "../stats/Index"
import { useHistory, useLocation } from "react-router-dom"
import TypesIndex from "../TypesIndex"
import Dashboard from "../dashboard/Index"
import { fromInfo, trans } from "../../../../_providers/Translation"
import Help from "../../../partials/Help"
import Api from "../../../../_helpers/Api"
import Warranty from "../list/Warranty"
import ActivateWarranty from "../list/ActivateWarranty"
import WarrantyServicing from "../list/WarrantyServicing"
import Question from "../../../modals/messages/Question"
import Success from "../../../modals/messages/Success"
import GuaranteesList from "./GuaranteesList"
import ServicingList from "./ServicingList"
import Contracts from "../../../modules/contracts/Index"
import { CONTRACT_TYPES } from "../../../modules/contracts/config"

function Guarantees(props) {
    const location = useLocation()
    const history = useHistory()

    const searchParams = new URLSearchParams(location.search)

    // Modals for the guarantees implementation (list & servicing tabs)
    const warrantyModalRef = useRef(null)
    const activateWarrantyModalRef = useRef(null)
    const deleteModalRef = useRef(null)
    const successModalRef = useRef(null)
    const servicingModalRef = useRef(null)

    const [refresh, setRefresh] = useState(0)

    const triggerRefresh = () => {
        setRefresh((prev) => prev + 1)
    }

    const tabs = [
        {
            id: 'dashboard',
            name: trans('tabs.dashboard'),
            component: Dashboard,
        },
        {
            id: 'list',
            name: trans('tabs.list'),
            component: GuaranteesList,
            hasRef: true,
            help: <Help>{fromInfo('contractors.list')}</Help>
        },
        // {
        //     id: 'types',
        //     name: trans('tabs.types'),
        //     component: TypesIndex,
        //     hasRef: true,
        //     help: <Help>{fromInfo('contractors.types')}</Help>
        // },
        {
            id: 'stats',
            name: trans('tabs.stats'),
            component: Stats,
            help: <Help>{fromInfo('contractors.stats')}</Help>
        },
        {
            id: 'servicing',
            name: trans('tabs.servicedDevices'),
            component: ServicingList,
            hasRef: true,
        },
        {
            id: 'contracts',
            name: trans('tabs.contracts'),
            component: Contracts,
            hasRef: true,
            props: {
                typeId: CONTRACT_TYPES.WARRANTY,
            },
        },
    ]

    const tab = searchParams.get('tab') ? searchParams.get('tab') : tabs[0].id

    const componentRef = useRef(null)

    const hasAddButton = searchParams.get('types') !== 'calendar'

    const [state, setState] = useState({
        tab: '',
        index: null,
        loading: true,
        data: null
    })

    useEffect(() => {

        setState(prev => ({
            ...prev,
            loading: true,
            tab: tab,
            index: findTabIndex(tab)
        }))

    }, [location.search])

    const handleTab = id => {

        let index = findTabIndex(id)

        setState(prev => ({
            ...prev,
            tab: id,
            index: index
        }))

        history.push('?tab=' + id)
    }

    const findTabIndex = id => {
        let index = tabs.findIndex(t => {
            return t.id === id
        })

        return index
    }

    const handleAdd = () => {
        // The guarantees tabs manage their own modals, the copied tabs use the ref pattern
        if (state.tab === 'list') {
            warrantyModalRef.current && warrantyModalRef.current.open()
            return
        }

        if (state.tab === 'servicing') {
            servicingModalRef.current && servicingModalRef.current.open()
            return
        }

        let modal = componentRef.current

        if (modal) {
            modal.add()
        }
    }

    const handleAddInvoice = () => {
        history.push('/accounting/add')
    }

    // Guarantees tab handlers
    const handleEditWarranty = (id) => {
        warrantyModalRef.current.open(id)
    }

    const handleDeleteWarranty = (id) => {
        let modal = deleteModalRef.current

        modal.open(id)

        modal.onSuccess(() => {
            handleDestroyWarranty(id)
        })
    }

    const handleDestroyWarranty = (id) => {
        Api.post("contractors/warranties/delete", { id }).then(() => {
            triggerRefresh()
        })
    }

    const handleActivateWarranty = (id) => {
        activateWarrantyModalRef.current.open(id)
    }

    const handleRenewWarranty = (id) => {
        Api.post("contractors/warranties/renew", { id }).then(() => {
            successModalRef.current.open()
            triggerRefresh()
        })
    }

    const handleDeactivareWarranty = (id) => {
        Api.post("contractors/warranties/deactivate", { id }).then(() => {
            successModalRef.current.open()
            triggerRefresh()
        })
    }

    // Servicing tab handlers
    const handleEditServicing = (id) => {
        servicingModalRef.current.open(id)
    }

    const handleNewServicing = (id) => {
        servicingModalRef.current.open(id, 'new')
    }

    const handleDeleteServicing = (id) => {
        let modal = deleteModalRef.current

        modal.open(id)

        modal.onSuccess(() => {
            handleDestroyServicing(id)
        })
    }

    const handleDestroyServicing = (id) => {
        Api.delete(`contractors/appliance-servicing/${id}`).then(() => {
            triggerRefresh()
        })
    }

    const renderTabComponent = () => {
        if (!tabs[state.index]) {
            return null
        }

        if (state.tab === 'list') {
            return (
                <GuaranteesList
                    handleEdit={handleEditWarranty}
                    handleDelete={handleDeleteWarranty}
                    handleActivate={handleActivateWarranty}
                    handleRenew={handleRenewWarranty}
                    handleDeactivate={handleDeactivareWarranty}
                    refresh={refresh}
                />
            )
        }

        if (state.tab === 'servicing') {
            return (
                <ServicingList
                    handleEdit={handleEditServicing}
                    handleDelete={handleDeleteServicing}
                    handleNewServicing={handleNewServicing}
                    refresh={refresh}
                />
            )
        }

        return React.createElement(tabs[state.index].component, {
            ref: componentRef,
            ...(tabs[state.index].props || {})
        })
    }

    return (
        <div className="wrapper">
            <Warranty ref={warrantyModalRef} onSave={triggerRefresh} />
            <WarrantyServicing ref={servicingModalRef} onSave={triggerRefresh} />

            <ActivateWarranty
                ref={activateWarrantyModalRef}
                onSave={triggerRefresh}
            />

            <Question
                ref={deleteModalRef}
                mainMessage={trans("headings.confirmDelete")}
                secondaryMessage={trans("subheadings.confirmDelete")}
            />

            <Success ref={successModalRef} mainMessage={trans("messages.success")} />

            <div className="top-part">
                <div className="left">
                    <h1 className="page-heading">
                        {trans('headings.guarantees')}
                    </h1>
                    <p className="page-subheading">
                        {trans('subheadings.guarantees')}
                    </p>
                </div>
                <div
                    className="right"
                    style={{
                        display: "flex"
                    }}
                >
                    <button className="button-add" onClick={handleAddInvoice}>
                        {trans('buttons.invoice')}
                    </button>
                    {tabs[state.index] && tabs[state.index].hasRef && hasAddButton
                        ?
                        <button
                            className="button-add"
                            onClick={handleAdd}
                            style={{
                                marginLeft: "8px"
                            }}
                        >
                            {trans('buttons.add')}
                        </button>
                        :
                        <>
                        </>
                    }
                </div>
            </div>

            <FilterTabs
                tabs={tabs}
                tab={state.tab}
                handleTab={handleTab}
            />

            {renderTabComponent()}

        </div>
    )
}

export default Guarantees
